import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './rednote-app-launcher.js';
import { bloggerApi, bloggerAuth } from '../services/api';
import BloggerLogin from './BloggerLogin';
import { useLightbox } from '../components/Lightbox';

function errMsg(e, fallback) {
  return e?.response?.data?.error || fallback;
}

export default function SubmitPage() {
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const scope = merchantId ? 'store' : 'universal';

  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState('');
  const [step, setStep] = useState(bloggerAuth.token() ? 'editor' : 'login');

  useEffect(() => {
    const p = scope === 'store' ? bloggerApi.merchant(merchantId) : bloggerApi.universal();
    p.then(setMeta)
      .catch((e) => setFatal(errMsg(e, '加载失败，请重新扫码')))
      .finally(() => setLoading(false));
  }, [merchantId, scope]);

  if (loading) return <div style={styles.centerPage}><img src="/favicon.png" alt="logo" style={styles.logoImg} /></div>;
  if (fatal) {
    return (
      <div style={styles.errorPage}>
        <div style={styles.errorIcon}>!</div>
        <p style={styles.errorText}>{fatal}</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <img src="/favicon.png" alt="券宝宝" style={styles.topLogo} />
        <span style={styles.topBrand}>券宝宝 · 投稿</span>
        <button style={styles.homeBtn} onClick={() => navigate('/blogger')}>主页</button>
      </div>
      {step === 'login' && (
        <BloggerLogin onDone={(needProfile) => setStep(needProfile ? 'profile' : 'editor')} />
      )}
      {step === 'profile' && <Profile onDone={() => setStep('editor')} />}
      {step === 'editor' && <Editor scope={scope} meta={meta} merchantId={merchantId} />}
    </div>
  );
}

// ── 资料补全（本地保存）─────────────────────
function Profile({ onDone }) {
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState('');
  const [intro, setIntro] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const fileRef = useRef(null);

  const onAvatar = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setBusy(true);
    try { setAvatar(await bloggerApi.upload(f)); } catch { setStatus('头像上传失败'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!nickname.trim()) { setStatus('请填写昵称'); return; }
    setBusy(true);
    try { await bloggerApi.saveProfile({ nickname: nickname.trim(), avatar, intro: intro.trim() }); onDone(); }
    catch (e) { setStatus(errMsg(e, '保存失败')); }
    finally { setBusy(false); }
  };

  return (
    <div style={styles.scrollArea}>
      <div style={styles.card}>
        <p style={styles.hint}>完善资料（仅首次）</p>
        <div style={styles.avatarRow}>
          <div style={styles.avatarBox} onClick={() => fileRef.current?.click()}>
            {avatar ? <img src={avatar} alt="" style={styles.avatarImg} /> : <span style={styles.avatarPlus}>+</span>}
          </div>
          <span style={styles.avatarLabel}>点击上传头像（选填）</span>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatar} />
        <input style={styles.input} placeholder="昵称" value={nickname} onChange={(e) => setNickname(e.target.value)} />
        <textarea style={{ ...styles.textarea, minHeight: 80 }} placeholder="个人简介（选填）"
          value={intro} onChange={(e) => setIntro(e.target.value)} />
        {status && <p style={styles.statusText}>{status}</p>}
        <button style={{ ...styles.primaryBtn, ...(busy ? styles.btnDisabled : {}) }} onClick={save} disabled={busy}>保存并继续</button>
      </div>
    </div>
  );
}

// ── 投稿编辑器 ───────────────────────────────
function Editor({ scope, meta, merchantId }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const lb = useLightbox();
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const fileRef = useRef(null);

  const isReview = scope === 'store' && (meta?.targetChannel || 'review') === 'review';

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = '';
    if (!files.length) return;
    setUploading(true); setStatus('');
    try { for (const f of files) { const url = await bloggerApi.upload(f); setImages((p) => [...p, url]); } }
    catch (err) { setStatus(errMsg(err, '图片上传失败')); }
    finally { setUploading(false); }
  };
  const removeImg = (i) => setImages((p) => p.filter((_, idx) => idx !== i));

  const publish = async () => {
    if (!content.trim()) { setStatus('请填写正文内容'); return; }
    if (images.length === 0) { setStatus('请至少上传一张图片'); return; }
    setBusy(true); setStatus('');
    try {
      const res = await bloggerApi.submit({
        scope, merchantId,
        title: title.trim(), content: content.trim(), images,
        pushedToXhs: true,
      });
      const launcher = window.RednoteAppLauncher;
      if (launcher) {
        const httpImgs = images.filter((u) => /^https?:\/\//i.test(u));
        const result = launcher.openPublish({ type: 'normal', title: title.trim(), content: content.trim(), images: httpImgs });
        if (!result.ok) {
          setStatus(result.reason === 'in_app_browser'
            ? '投稿已提交。请点右上角在系统浏览器打开后再发布到小红书'
            : '投稿已提交。当前环境不支持跳转小红书，请在系统浏览器打开');
          return;
        }
      }
      const tail = res.qbbStatus === 'sent' ? '，已同步到券宝宝'
        : res.autoPublish === false ? '（已进入券宝宝内容池，待运营发布）'
        : res.qbbStatus === 'disabled' ? '（券宝宝同步待接口开通）'
        : '（券宝宝同步失败，稍后由运营处理）';
      setStatus(`投稿已提交${tail}，正在跳转小红书…`);
    } catch (e) { setStatus(errMsg(e, '提交失败，请重试')); }
    finally { setBusy(false); }
  };

  return (
    <div style={styles.scrollArea}>
      <div style={styles.card}>
        <div style={styles.campaignLine}>
          {scope === 'store' ? `${meta?.merchantName || ''} · ${meta?.name || ''}` : '通用投稿'}
          {isReview ? ' · 店铺评价' : ' · 帖子'}
        </div>
        <input style={styles.input} placeholder="标题（选填）" value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea style={styles.textarea} placeholder="写点什么…（正文）" value={content} onChange={(e) => setContent(e.target.value)} />

        <div style={styles.label}>图片</div>
        <div style={styles.imgGrid}>
          {images.map((url, i) => (
            <div key={i} style={styles.imgCell}>
              <img src={url} alt="" style={styles.imgThumb} onClick={() => lb.open(images, i)} />
              <span style={styles.imgRemove} onClick={() => removeImg(i)}>×</span>
            </div>
          ))}
          <div style={styles.addCell} onClick={() => fileRef.current?.click()}>
            <span style={styles.addPlus}>{uploading ? '…' : '+'}</span>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onFiles} />

        {isReview && <p style={styles.reviewTip}>较长的内容发布时会自动整理成一段自然的店铺评价</p>}
        {status && <div style={styles.statusBox}><span style={styles.statusText}>{status}</span></div>}
      </div>
      {lb.node}
      <div style={{ height: 100 }} />
      <div style={styles.bottomBar}>
        <button style={{ ...styles.primaryBtn, ...(busy || uploading ? styles.btnDisabled : {}) }}
          onClick={publish} disabled={busy || uploading}>提交并发布到小红书</button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto', position: 'relative' },
  centerPage: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' },
  logoImg: { width: 72, height: 72, borderRadius: 18 },
  errorPage: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff', padding: 32 },
  errorIcon: { width: 56, height: 56, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#FA1C51', marginBottom: 16 },
  errorText: { fontSize: 15, color: '#666', textAlign: 'center', lineHeight: 1.6, margin: 0 },
  topBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 },
  topLogo: { width: 28, height: 28, borderRadius: 7 },
  topBrand: { fontSize: 15, fontWeight: 700, color: '#111' },
  homeBtn: { marginLeft: 'auto', padding: '6px 12px', fontSize: 14, color: '#FA1C51', background: '#fff7f8', border: '1px solid #ffd6de', borderRadius: 8 },
  scrollArea: { flex: 1, overflowY: 'auto' },
  card: { background: '#fff', margin: '10px 0', padding: '16px' },
  campaignLine: { fontSize: 13, color: '#999', marginBottom: 12 },
  hint: { fontSize: 13, color: '#888', margin: '4px 0 16px', lineHeight: 1.6 },
  input: { width: '100%', boxSizing: 'border-box', padding: '13px 14px', fontSize: 15, border: '1px solid #eee', borderRadius: 10, background: '#fafafa', marginBottom: 12, outline: 'none' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '13px 14px', fontSize: 15, border: '1px solid #eee', borderRadius: 10, background: '#fafafa', marginBottom: 12, minHeight: 160, resize: 'vertical', lineHeight: 1.7, outline: 'none' },
  select: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 15, border: '1px solid #eee', borderRadius: 10, background: '#fafafa', marginBottom: 12 },
  codeRow: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 },
  codeBtn: { padding: '13px 14px', fontSize: 14, color: '#FA1C51', background: '#fff7f8', border: '1px solid #ffd6de', borderRadius: 10, whiteSpace: 'nowrap' },
  avatarRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
  avatarBox: { width: 64, height: 64, borderRadius: '50%', background: '#fafafa', border: '1px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarPlus: { fontSize: 24, color: '#bbb' },
  avatarLabel: { fontSize: 13, color: '#999' },
  label: { fontSize: 13, color: '#666', margin: '6px 0 8px' },
  imgGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  imgCell: { position: 'relative', width: 80, height: 80 },
  imgThumb: { width: 80, height: 80, objectFit: 'cover', borderRadius: 8 },
  imgRemove: { position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 15, lineHeight: '20px', textAlign: 'center' },
  addCell: { width: 80, height: 80, borderRadius: 8, border: '1px dashed #ddd', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  addPlus: { fontSize: 28, color: '#bbb' },
  reviewTip: { fontSize: 12, color: '#999', margin: '4px 0 0', lineHeight: 1.5 },
  statusBox: { margin: '12px 0 0', background: '#fffbeb', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #f59e0b' },
  statusText: { fontSize: 13, color: '#92400e' },
  bottomBar: { position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, padding: '12px 16px 24px', background: '#fff', borderTop: '1px solid #f0f0f0', boxSizing: 'border-box' },
  primaryBtn: { width: '100%', padding: '16px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 14, fontSize: 17, fontWeight: 700, letterSpacing: 1 },
  btnDisabled: { opacity: 0.5 },
};
